import { isEmpty, pickBy } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useCompListingDisabledSections } from 'src/hooks/useCompListingDisabledSections';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { onSectionSelctionWithMyRowDefaults } from 'src/utils/venueUtils';
import {
  Feature,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
  SectionInfo,
  SelectedSectionSettings,
} from 'src/WebApiController';

import { ListingTab } from '../../components/listingDetailsUtils';
import { useListingDetailsContext } from '../../ListingDetailsModalV2Context';
import { useAutoPricingSidePanelContext } from '../AutoPricingSidePanel';
import { ListingAutoPricingSettingsFormContentWrapper } from './components/ListingAutoPricingSettingsFormContentWrapper/ListingAutoPricingSettingsFormContentWrapper';
import { useAutoPricingSettings } from './useAutoPricingSettings';

type ListingDetailsAutoPricingFormProps = {
  listing: ListingDetails;
};

export const ListingDetailsAutoPricingForm: React.FC<
  ListingDetailsAutoPricingFormProps
> = ({ listing }) => {
  const { methods, isLoading, onTabChange } = useListingDetailsContext();

  return (
    <FormProvider {...methods.autoPricing}>
      <ListingAutoPricingSettingsFormWrapper
        listing={listing}
        isLoading={isLoading}
        onTabChange={onTabChange}
      />
    </FormProvider>
  );
};

type ListingAutoPricingSettingsFormWrapperProps = {
  listing: ListingDetails;
  isLoading: boolean;
  onTabChange: (v: ListingTab) => void;
};

const ListingAutoPricingSettingsFormWrapper: React.FC<
  ListingAutoPricingSettingsFormWrapperProps
> = ({ listing, isLoading, onTabChange }) => {
  const { watch } = useFormContext<ListingDetailsAutoPricingSectionUpdates>();
  const { updateMetadata } = useAutoPricingSidePanelContext();
  const hasDefaultToMyRowOnSectionSelectionFeature = useUserHasFeature(
    Feature.DefaultToMyRowOnSectionSelection
  );

  const input = watch();

  const { compListingSelectedSectionSettings } = input;
  const {
    compListingMode,
    compListingFloor,
    compListingCeiling,
    onCompListingChange,
  } = useAutoPricingSettings(listing);
  const { venueMapInfo } = useEventMapContext();

  const { disabledSectionIds } = useCompListingDisabledSections(
    listing,
    false,
    compListingMode,
    compListingFloor,
    compListingCeiling
  );

  const onSelectedSectionsChange = useCallback(
    (selectedSections: SectionInfo[]) => {
      let compListingSelectedSectionSettingsNew: SelectedSectionSettings | null =
        null;

      if (hasDefaultToMyRowOnSectionSelectionFeature) {
        compListingSelectedSectionSettingsNew =
          onSectionSelctionWithMyRowDefaults(
            compListingSelectedSectionSettings,
            selectedSections,
            venueMapInfo?.sections ?? [],
            listing
          );
      } else {
        // Restructure the id filters.
        const sectionIdFilter = selectedSections.map((s) => s.id);
        const selectedRowIds = selectedSections
          .flatMap(({ rows }) => rows)
          .map(({ id }) => id);
        const rowIdFilter = [];
        if (isEmpty(compListingSelectedSectionSettings?.rowIdFilter)) {
          rowIdFilter.push(...selectedRowIds);
        } else {
          rowIdFilter.push(
            ...(compListingSelectedSectionSettings?.rowIdFilter?.filter(
              (rowId) => selectedRowIds.includes(rowId)
            ) ?? [])
          );
        }
        const sectionRowIdFilter = pickBy(
          Object.assign(
            {},
            compListingSelectedSectionSettings?.sectionRowIdFilter ?? {}
          ),
          (_, key) => sectionIdFilter.includes(parseInt(key))
        );
        compListingSelectedSectionSettingsNew = {
          ...compListingSelectedSectionSettings,
          sectionIdFilter,
          rowIdFilter,
          sectionRowIdFilter,
        } as SelectedSectionSettings;
      }
      onCompListingChange({
        compListingOnlyForSameZoneEnabledNew: false,
        compListingOnlyForSelectedSectionsEnabledNew: true,
        compListingSelectedSectionSettingsNew,
      });
    },
    [
      compListingSelectedSectionSettings,
      hasDefaultToMyRowOnSectionSelectionFeature,
      listing,
      onCompListingChange,
      venueMapInfo?.sections,
    ]
  );

  useEffect(() => {
    updateMetadata({
      input,
      sectionIds: compListingSelectedSectionSettings?.sectionIdFilter ?? [],
      disabledSectionIds,
      onSelectedSectionsChange,
    });
  }, [
    compListingSelectedSectionSettings?.sectionIdFilter,
    disabledSectionIds,
    onSelectedSectionsChange,
    input,
    updateMetadata,
  ]);

  return (
    <ListingAutoPricingSettingsFormContentWrapper
      listing={listing}
      disabled={isLoading}
      onSwitchToTab={onTabChange}
    />
  );
};
